import { useState, useEffect } from 'react';
import { Button } from '@/components/ui/button';
import { Checkbox } from '@/components/ui/checkbox';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import { Loader2, Wand2, Save } from 'lucide-react';
import { useToast } from '@/lib/hooks/use-toast';
import { freightRouteService } from '../services/freight-route-service';
import { IncotermConfig, INCOTERM_FIELD_LABELS } from '../types/freight-route.types';

const COST_FIELDS = [
  'paysInlandOrigin',
  'paysInternational',
  'paysInlandDest',
  'paysHandling',
  'paysInsurance',
  'paysDuties',
] as const;

type CostField = (typeof COST_FIELDS)[number];

export function IncotermConfigTable() {
  const { toast } = useToast();
  const [configs, setConfigs] = useState<IncotermConfig[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [isSaving, setIsSaving] = useState(false);
  const [isSeeding, setIsSeeding] = useState(false);
  const [changes, setChanges] = useState<Record<string, Partial<Record<CostField, boolean>>>>({});

  const fetchConfigs = async () => {
    setIsLoading(true);
    try {
      const data = await freightRouteService.getIncotermConfigs();
      setConfigs(data);
      setChanges({});
    } catch (error: any) {
      toast({ title: 'Error', description: error.message, variant: 'destructive' });
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    fetchConfigs();
  }, []);

  const handleSeedDefaults = async () => {
    setIsSeeding(true);
    try {
      await freightRouteService.seedIncotermDefaults();
      toast({
        title: 'Incoterms configurados',
        description: '11 configuraciones ICC 2020 creadas',
        variant: 'success',
      });
      fetchConfigs();
    } catch (error: any) {
      toast({ title: 'Error', description: error.message, variant: 'destructive' });
    } finally {
      setIsSeeding(false);
    }
  };

  const handleToggle = (configId: string, field: CostField, currentValue: boolean) => {
    setChanges((prev) => ({
      ...prev,
      [configId]: {
        ...prev[configId],
        [field]: !currentValue,
      },
    }));
  };

  const getEffectiveValue = (config: IncotermConfig, field: CostField): boolean => {
    if (changes[config.id] && changes[config.id][field] !== undefined) {
      return changes[config.id][field]!;
    }
    return config[field];
  };

  const hasChanges = Object.keys(changes).length > 0;

  const handleSaveChanges = async () => {
    setIsSaving(true);
    try {
      for (const [configId, fieldChanges] of Object.entries(changes)) {
        await freightRouteService.updateIncotermConfig(configId, fieldChanges);
      }
      toast({ title: 'Cambios guardados', variant: 'success' });
      fetchConfigs();
    } catch (error: any) {
      toast({ title: 'Error', description: error.message, variant: 'destructive' });
    } finally {
      setIsSaving(false);
    }
  };

  if (isLoading) {
    return (
      <div className="flex items-center justify-center py-12">
        <Loader2 className="w-6 h-6 animate-spin text-muted-foreground" />
      </div>
    );
  }

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between">
        <p className="text-sm text-muted-foreground">
          Define qué costos de flete paga el <strong>comprador</strong> (importador) según el
          incoterm. Los casilleros marcados indican que el comprador es responsable de ese costo.
        </p>
        <div className="flex gap-2">
          {configs.length === 0 && (
            <Button variant="outline" onClick={handleSeedDefaults} disabled={isSeeding}>
              {isSeeding ? (
                <Loader2 className="w-4 h-4 mr-2 animate-spin" />
              ) : (
                <Wand2 className="w-4 h-4 mr-2" />
              )}
              Cargar Defaults ICC 2020
            </Button>
          )}
          {hasChanges && (
            <Button onClick={handleSaveChanges} disabled={isSaving}>
              {isSaving ? (
                <Loader2 className="w-4 h-4 mr-2 animate-spin" />
              ) : (
                <Save className="w-4 h-4 mr-2" />
              )}
              Guardar Cambios
            </Button>
          )}
        </div>
      </div>

      {configs.length === 0 ? (
        <div className="text-center py-12 text-muted-foreground">
          No hay configuraciones de incoterms. Usa "Cargar Defaults ICC 2020" para empezar.
        </div>
      ) : (
        <div className="border rounded-lg overflow-x-auto">
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead className="sticky left-0 bg-background z-10 min-w-[100px]">
                  Incoterm
                </TableHead>
                <TableHead className="sticky left-[100px] bg-background z-10 min-w-[180px]">
                  Descripcion
                </TableHead>
                {COST_FIELDS.map((field) => (
                  <TableHead key={field} className="text-center min-w-[120px]">
                    {INCOTERM_FIELD_LABELS[field]}
                  </TableHead>
                ))}
              </TableRow>
            </TableHeader>
            <TableBody>
              {configs.map((config) => {
                const hasConfigChanges = !!changes[config.id];
                return (
                  <TableRow
                    key={config.id}
                    className={hasConfigChanges ? 'bg-yellow-50 dark:bg-yellow-950/20' : ''}
                  >
                    <TableCell className="sticky left-0 bg-inherit z-10 font-bold text-base">
                      {config.incoterm}
                    </TableCell>
                    <TableCell className="sticky left-[100px] bg-inherit z-10 text-sm text-muted-foreground">
                      {config.description || '—'}
                    </TableCell>
                    {COST_FIELDS.map((field) => {
                      const value = getEffectiveValue(config, field);
                      const isChanged = changes[config.id]?.[field] !== undefined;
                      return (
                        <TableCell key={field} className="text-center">
                          <div className="flex justify-center">
                            <Checkbox
                              checked={value}
                              onCheckedChange={() => handleToggle(config.id, field, value)}
                              className={isChanged ? 'ring-2 ring-yellow-400' : ''}
                            />
                          </div>
                        </TableCell>
                      );
                    })}
                  </TableRow>
                );
              })}
            </TableBody>
          </Table>
        </div>
      )}

      {/* Legend */}
      {configs.length > 0 && (
        <div className="flex gap-4 text-xs text-muted-foreground">
          <div className="flex items-center gap-1">
            <Checkbox checked disabled className="w-3 h-3" /> = Comprador paga
          </div>
          <div className="flex items-center gap-1">
            <Checkbox disabled className="w-3 h-3" /> = Vendedor cubre
          </div>
        </div>
      )}
    </div>
  );
}
